import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, Github, Calendar, Tag, ArrowUpRight } from 'lucide-react';
import { useProjects } from '../contexts/ProjectsContext';
import SEO from '../components/SEO';
import { Project } from '../types';
import { useTranslation } from '../contexts/LanguageContext';

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { projects } = useProjects();
  const [project, setProject] = useState<Project | null>(null);
  const navigate = useNavigate();
  const { t, lang } = useTranslation();

  useEffect(() => {
    const foundProject = projects.find(p => p.id === id);
    if (foundProject) {
      setProject(foundProject);
    } else if (projects.length > 0 && !foundProject) {
      navigate('/projects', { replace: true });
    }
  }, [id, projects, navigate]);

  if (!project) {
    return (
      <div className="flex justify-center items-center h-64 mt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  // Format date
  const locale = lang === 'fr' ? 'fr-FR' : 'en-US';
  const formattedDate = new Date(project.createdAt).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
  });

  return (
    <>
      <SEO
        title={project.title}
        description={project.description}
        keywords={[...project.technologies, 'project', 'portfolio']}
        ogImage={project.imageUrl}
        ogUrl={`/projects/${project.id}`}
      />

      <div className="max-w-4xl mx-auto my-20 ">

        <button className="hover:text-sky-700 text-white font-bold px-6 py-3 rounded-2xl shadow-xl shadow-sky-500/25 hover:shadow-sky-500/40 hover:scale-[1.03] active:scale-95 transition-all duration-500 group text-lg flex items-start mb-8">
          <ArrowLeft className="ml-4 h-6 w-6 transition-transform group-hover:-translate-x-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
          <Link
            to="/projects"
          >
            {t('projectDetail.back')}
          </Link>
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-sky-950 bg-opacity-65 rounded-2xl shadow-md overflow-hidden">
            <div className="h-80 overflow-hidden">
              <img
                src={project.imageUrl}
                alt={project.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="p-8">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-white mb-4">{project.title}</h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-primary-200 mb-4">
                  <div className="flex items-center">
                    <Calendar size={16} className="mr-1" />
                    {formattedDate}
                  </div>
                  {project.featured && (
                    <span className="bg-sky-950 bg-opacity-65 text-sky-500 px-2.5 py-1 rounded-full text-xs font-semibold">
                      {t('projectDetail.featuredBadge')}
                    </span>
                  )}
                </div>

                <p className="text-lg text-white leading-relaxed">
                  {project.description}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-3 flex items-center">
                  <Tag size={18} className="mr-2 text-sky-500" />
                  {t('projectDetail.techTitle')}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="bg-sky-950 bg-opacity-65 text-white px-3 py-1.5 rounded-full text-sm font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    // rel="noopener noreferrer"
                    className="flex items-center text-white"
                  >
                    <button className="bg-gradient-to-r from-sky-600 via-sky-500 to-sky-700 hover:from-sky-700 hover:via-sky-600 hover:to-sky-800 text-white font-bold px-6 py-3 rounded-2xl shadow-xl shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.03] active:scale-95 transition-all duration-500 group text-lg flex items-center mx-auto">
                      <ExternalLink size={18} className="mr-2 ml-4 h-6 w-6 transition-transform group-hover:-translate-x-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
                      {t('projectDetail.liveDemo')}
                    </button>
                  </a>

                )}
                {project.live && (
                  <a
                    href={project.live}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-white "
                  >
                    <button
                      className="bg-gradient-to-r from-primary-200 via-white to-primary-300 hover:from-primary-300 hover:via-primary-100 hover:to-primary-400 text-sky-600 font-bold px-7 py-3 rounded-2xl shadow-xl  hover:shadow-sky-700 hover:scale-[1.03] active:scale-95 transition-all duration-500 group text-lg flex items-center mx-auto">
                      <ArrowUpRight size={18} className="mr-2 h-6 w-6 transition-transform group-hover:-translate-y-1 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
                      {t('projectDetail.liveVersion')}
                    </button>
                  </a>
                )}

                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center"
                  >
                    <button
                      className="bg-gradient-to-r from-primary-200 via-white to-primary-300 hover:from-primary-300 hover:via-primary-100 hover:to-primary-400 text-sky-600 font-bold px-7 py-3 rounded-2xl shadow-xl  hover:shadow-sky-700 hover:scale-[1.03] active:scale-95 transition-all duration-500 group text-lg flex items-center mx-auto">
                      <Github size={18} className="mr-2 h-6 w-6 transition-transform animate-spin drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
                    {t('projectDetail.viewSource')} 
                    </button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Project Navigation - Next/Previous */}
        <div className="mt-12">
          <h3 className="text-lg font-semibold text-white mb-4">
            {t('projectDetail.otherProjects')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {projects.filter(p => p.id !== project.id).slice(0, 2).map((relatedProject) => (
              <Link
                key={relatedProject.id}
                to={`/projects/${relatedProject.id}`}
                className="group bg-sky-950 bg-opacity-65 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="h-40 overflow-hidden">
                  <img
                    src={relatedProject.imageUrl}
                    alt={relatedProject.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <h4 className="font-medium text-white group-hover:text-sky-500 transition-colors">
                    {relatedProject.title}
                  </h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectDetailPage;